import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useGetBoundaryFillInfo } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryFillInfo';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { boundaryTerritoryGroupActivate } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import { useGetBoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useActiveBTGIdPerBoundaryGroup } from '~/store/mapSettings/toolsState/boundary/activeBTG.selectors';
import type { SortedBTGs } from '../boundaryTools.types';

export const useBTGFillData = ({
  boundaryGroupId,
  boundaryTerritoryGroups,
}: {
  boundaryGroupId: BoundaryGroupId;
  boundaryTerritoryGroups: SortedBTGs;
}) => {
  const dispatch = useDispatch();
  const getBoundaryFillInfo = useGetBoundaryFillInfo();
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const activeBTGId = useActiveBTGIdPerBoundaryGroup(boundaryGroupId) ?? boundaryTerritoryGroups[1]?.boundaryTerritoryGroupId ?? boundaryTerritoryGroups[0]?.boundaryTerritoryGroupId;
  const activeBoundaryTerritoryGroup = getBoundaryTerritoryGroupById(activeBTGId) ?? boundaryTerritoryGroups[1] ?? boundaryTerritoryGroups[0];

  const hasMoreThanOneFills = boundaryTerritoryGroups.filter(notNullsy).length > 1;

  const allBTGDropDownOptions = boundaryTerritoryGroups
    .map((btg, index) => {
      if (!btg) {
        return null;
      }

      const boundaryFillInfo = getBoundaryFillInfo(btg, index);

      return {
        ...boundaryFillInfo,
        fillOrderNumber: hasMoreThanOneFills ? boundaryFillInfo.fillOrderNumber : null,
        boundaryTerritoryGroup: btg,
      } as const;
    }).filter(notNullsy);

  const setActiveBTGId = useCallback((boundaryTerritoryGroup: BoundaryTerritoryGroup) => {
    dispatch(boundaryTerritoryGroupActivate(boundaryTerritoryGroup));
  }, [dispatch]);

  const activeBTGOption = allBTGDropDownOptions.find(
    btgOption => btgOption.boundaryTerritoryGroup.boundaryTerritoryGroupId
      === activeBoundaryTerritoryGroup.boundaryTerritoryGroupId
  );
  const filteredBTGDropDownOptions = allBTGDropDownOptions
    .filter(btgOption => btgOption !== activeBTGOption);

  const allowFillChange = (activeBoundaryTerritoryGroup.settings.boundaryTerritoryType !== BoundaryTerritoryType.Manual) ||
    (filteredBTGDropDownOptions.length !== 0);

  const isOnlyNoFillOption = allBTGDropDownOptions.length === 1 && allBTGDropDownOptions[0]?.isNoFill;

  return {
    activeBTGOption,
    activeBoundaryTerritoryGroup,
    allowFillChange,
    filteredBTGDropDownOptions,
    setActiveBTGId,
    isOnlyNoFillOption,
  };
};
